import React, { useState, useEffect } from 'react';
import { fetchData } from '../../api';

export default function ApplyNow() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        position: '',
        coverLetter: '',
        cv: null,
    });

    const [pageData, setPageData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    useEffect(() => {
        fetchData("apply-now-pages", true).then(data => {
            setPageData(data?.[0]);
        });
    }, []);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert('Bestand is te groot. Maximum grootte is 10MB.');
                e.target.value = '';
                return;
            }
            setFormData(prev => ({ ...prev, cv: file }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('position', formData.position);
            formDataToSend.append('coverLetter', formData.coverLetter);
            formDataToSend.append('cv', formData.cv);

            const response = await fetch(`${process.env.REACT_APP_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}/api/contact-email/job-application`, {
                method: 'POST',
                body: formDataToSend
            });

            const result = await response.json();

            if (response.ok) {
                setSubmitStatus({ type: 'success', message: 'Sollicitatie succesvol verzonden! We nemen zo snel mogelijk contact met u op.' });
                setFormData({ name: '', email: '', phone: '', position: '', coverLetter: '', cv: null });
                const fileInput = document.getElementById('cv');
                if (fileInput) fileInput.value = '';
            } else {
                setSubmitStatus({ type: 'error', message: result.error || 'Er is een fout opgetreden. Probeer het later opnieuw.' });
            }
        } catch (error) {
            console.error('Submit error:', error);
            setSubmitStatus({ type: 'error', message: 'Er is een fout opgetreden. Controleer uw internetverbinding en probeer het opnieuw.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white py-12 mb-20">
            <div className="container">
                <h2 className="font-semibold text-center mb-4">{pageData?.Title}</h2>
                <h4 className="text-center mb-10">{pageData?.Subtitle}</h4>
                
                {submitStatus && (
                    <div className={`max-w-md mx-auto mb-6 p-4 rounded-lg ${
                        submitStatus.type === 'success' 
                            ? 'bg-green-50 border border-green-200 text-green-800' 
                            : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                        {submitStatus.message}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="max-w-md mx-auto bg-gray-100 p-6 rounded-lg shadow-lg">
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" htmlFor="name">{pageData?.NameLabel || "Volledige Naam"}</label>
                        <input
                            type="text"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                            className="w-full p-2 border border-gray-300 rounded"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" htmlFor="email">{pageData?.EmailLabel || "E-mail"}</label>
                        <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                            className="w-full p-2 border border-gray-300 rounded"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" htmlFor="phone">{pageData?.PhoneLabel || "Telefoonnummer"}</label>
                        <input
                            type="tel"
                            id="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                            className="w-full p-2 border border-gray-300 rounded"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" htmlFor="position">Positie</label>
                        <input
                            type="text"
                            id="position"
                            value={formData.position}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            className="w-full p-2 border border-gray-300 rounded"
                            placeholder="Bijv. Timmerman, Projectleider, etc."
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" htmlFor="coverLetter">Motivatiebrief (optioneel)</label>
                        <textarea
                            id="coverLetter"
                            value={formData.coverLetter}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            rows={4}
                            className="w-full p-2 border border-gray-300 rounded resize-none"
                            placeholder="Vertel ons waarom u geïnteresseerd bent in deze positie..."
                        ></textarea>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2" htmlFor="cv">{pageData?.CVLabel || "CV (PDF, DOC, DOCX)"}</label>
                        <input
                            type="file"
                            id="cv"
                            onChange={handleFileChange}
                            required
                            disabled={isSubmitting}
                            accept=".pdf,.doc,.docx"
                            className="w-full p-2 border border-gray-300 rounded"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum bestandsgrootte: 10MB</p>
                    </div>
                    <button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="w-full bg-[#27352C] text-white py-3 hover:bg-[#1a241e] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Verzenden...
                            </div>
                        ) : (
                            pageData?.SubmitButtonText || "Sollicitatie Verzenden"
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
